import { createAsyncThunk } from "@reduxjs/toolkit";
import { resetIsLoading, resetUiAlertState, setIsLoading, setUiAlertState } from "src/store/common-slices/uiSlice";
import { resetContainerState } from "src/store/common-slices/getContainerSlice";
import { resetCloseContainerFlag } from "src/store/common-slices/closeContainerSlice";
import { AlertLevels } from "src/models/alert-model";
import * as ErrorConstants from "src/localization/string-ids/error-constants";
import { WIRELESS_PRINT_SUCCESS } from "src/localization/string-ids/success-constants";
import { RIVApiMethods } from "src/constants/api-method-constants";
import RIVBFFService from "src/services/riv-bff-service";
import { buildCreateBarcodeLabelInput } from "src/models/CreateBarcodeLabel";
import { admPrint } from "src/utils/adm-print-utils";
import { awsRum } from "src/serviceClients/aws-rum/awsRum-Service";

export interface WirelessPrintInput {
    containerId: string;
    printerIP: string;
    warehouseId: string;
    associate: string;
    locale: string;
}

/**
 * Redux thunk for wireless printing at MoC/AFT sites via ADMPrintApp.
 * Follows the same loading/alert pattern as the existing printContainer thunk.
 *
 * Flow: createBarcodeLabel API → get ZPL → admPrint (WLS → ADMPrintApp → printer)
 */
export const wirelessPrintContainer = createAsyncThunk<
    void,
    WirelessPrintInput>(
    'print/wirelessPrintContainer',
    async (input: WirelessPrintInput, { dispatch }) => {
        dispatch(resetUiAlertState());
        dispatch(setIsLoading());
        try {
            // Step 1: Get ZPL from backend
            const response = await RIVBFFService.triggerCreateBarcodeLabelAPI(
                buildCreateBarcodeLabelInput(
                    input.containerId,
                    input.warehouseId,
                    input.associate,
                    input.locale
                )
            );

            // Step 2: Validate ZPL response
            if (!response.label) {
                dispatch(resetIsLoading());
                awsRum.recordEvent('ADM_PRINT_VIA_IP', {
                    Status: 'FAILURE',
                    Latency: 0,
                    Warehouse: input.warehouseId,
                    Associate: input.associate,
                    PrinterIP: input.printerIP,
                    FailureReason: 'EMPTY_ZPL_RESPONSE'
                });
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: input.containerId,
                    alertMessageString: ErrorConstants.CREATE_BARCODE_LABEL_ERROR,
                    UIAction: RIVApiMethods.CREATE_BARCODE_LABEL,
                    warehouse: input.warehouseId,
                    associate: input.associate,
                }));
                return;
            }

            // Step 3: Send ZPL to printer via ADMPrintApp
            const printResult = await admPrint(
                response.label,
                input.printerIP,
                input.warehouseId,
                input.associate
            );

            dispatch(resetIsLoading());

            if (printResult.success) {
                dispatch(resetContainerState());
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.SUCCESS,
                    alertMessageString: WIRELESS_PRINT_SUCCESS + input.printerIP
                }));
                dispatch(resetCloseContainerFlag());
            } else {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: input.containerId,
                    alertMessageString: printResult.message,
                    UIAction: RIVApiMethods.CREATE_BARCODE_LABEL,
                    warehouse: input.warehouseId,
                    associate: input.associate,
                }));
            }
        } catch (error) {
            dispatch(resetIsLoading());
            awsRum.recordEvent('ADM_PRINT_VIA_IP', {
                Status: 'FAILURE',
                Latency: 0,
                Warehouse: input.warehouseId,
                Associate: input.associate,
                PrinterIP: input.printerIP,
                FailureReason: 'API_ERROR'
            });

            if (error && typeof error === 'object' && 'errorMessage' in error && 'errorStatus' in error) {
                const apiError = error as { errorMessage: string; errorStatus: number };
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: input.containerId,
                    alertMessageString: apiError.errorMessage || ErrorConstants.CREATE_BARCODE_LABEL_ERROR,
                    alertStatus: apiError.errorStatus,
                    UIAction: RIVApiMethods.CREATE_BARCODE_LABEL,
                    warehouse: input.warehouseId,
                    associate: input.associate,
                }));
            } else if (error instanceof Error) {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: input.containerId,
                    alertMessageString: error.message
                }));
            } else {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: input.containerId,
                    alertMessageString: ErrorConstants.CREATE_BARCODE_LABEL_ERROR
                }));
            }
        }
    }
);
